import { useState } from 'react';
import type { ChangeEvent, FormEvent } from 'react';

const SUBMIT_ADDR = 'https://readdy.ai/api/form/dbb3c148sc1eisn2tn3g';
const MAX_MESSAGE = 500;

type Errors = {
  name?: string;
  email?: string;
  subject?: string;
  message?: string;
};

const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/;

export default function ContactForm() {
  const [values, setValues] = useState({ name: '', email: '', subject: '', message: '' });
  const [errors, setErrors] = useState<Errors>({});
  const [status, setStatus] = useState<'idle' | 'sending' | 'ok' | 'error'>('idle');
  const [feedback, setFeedback] = useState('');

  const onChange = (e: ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    const { name, value } = e.target;
    setValues((v) => ({ ...v, [name]: value }));
    setErrors((prev) => ({ ...prev, [name]: undefined }));
    if (status !== 'idle') {
      setStatus('idle');
      setFeedback('');
    }
  };

  const validate = (v: typeof values): Errors => {
    const errs: Errors = {};
    if (!v.name.trim()) errs.name = 'Please enter your full name.';
    if (!v.email.trim()) errs.email = 'Please enter your email address.';
    else if (!EMAIL_RE.test(v.email.trim())) errs.email = 'Please enter a valid email address.';
    if (!v.subject.trim()) errs.subject = 'Please add a short subject.';
    if (!v.message.trim()) errs.message = 'Please write a message.';
    else if (v.message.length > MAX_MESSAGE) errs.message = 'Please keep your message under 500 characters.';
    return errs;
  };

  const onSubmit = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    const form = event.currentTarget;

    const errs = validate(values);
    setErrors(errs);
    if (Object.keys(errs).length) {
      setStatus('error');
      setFeedback('Please check the highlighted fields and try again.');
      return;
    }

    const data = new FormData(form);
    const honey = String(data.get('contact_alt') || '').trim();

    setStatus('sending');
    setFeedback('');

    if (honey) {
      // Silent success for bots.
      setStatus('ok');
      setFeedback('Thank you — your message has been received. We will be in touch.');
      form.reset();
      setValues({ name: '', email: '', subject: '', message: '' });
      return;
    }

    data.delete('contact_alt');

    const encode = (fd: FormData) => {
      const params = new URLSearchParams();
      fd.forEach((value, key) => {
        params.append(key, typeof value === 'string' ? value : '');
      });
      return params.toString();
    };

    try {
      const response = await fetch(SUBMIT_ADDR, {
        method: 'POST',
        headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
        body: encode(data),
      });
      const responseText = await response.text();
      let parsed: { code?: string; meta?: { message?: string; detail?: string }; message?: string } | null = null;
      try {
        parsed = JSON.parse(responseText);
      } catch {
        parsed = null;
      }
      const serverMsg = parsed?.meta?.message || parsed?.message || parsed?.meta?.detail || responseText || '';
      const ok = response.ok && parsed?.code === 'OK' && !/spam/i.test(serverMsg);

      if (ok) {
        setStatus('ok');
        setFeedback('Thank you — your message has been received. We will be in touch.');
        form.reset();
        setValues({ name: '', email: '', subject: '', message: '' });
      } else {
        setStatus('error');
        setFeedback(
          serverMsg && !/spam/i.test(serverMsg)
            ? `Your message could not be sent: ${serverMsg}`
            : 'Your message could not be sent just now. Please email sherri@therevefoundation.com directly.',
        );
      }
    } catch {
      setStatus('error');
      setFeedback('Your message could not be sent. Please email sherri@therevefoundation.com directly.');
    }
  };

  return (
    <form className="form-card" id="contact-form" data-readdy-form onSubmit={onSubmit} noValidate>
      <p className="form-title">Send a message</p>
      <p className="form-sub">We read every note personally. Fields marked with an asterisk are required.</p>

      <div className="form-grid">
        <div className="field">
          <label htmlFor="cf-name">Full name *</label>
          <input
            id="cf-name"
            name="name"
            type="text"
            autoComplete="name"
            placeholder="Your full name"
            value={values.name}
            onChange={onChange}
            className="box-border w-full min-w-0"
            aria-invalid={!!errors.name}
          />
          {errors.name && <span className="err">{errors.name}</span>}
        </div>

        <div className="field">
          <label htmlFor="cf-email">Email address *</label>
          <input
            id="cf-email"
            name="email"
            type="email"
            autoComplete="email"
            placeholder="you@example.com"
            value={values.email}
            onChange={onChange}
            className="box-border w-full min-w-0"
            aria-invalid={!!errors.email}
          />
          {errors.email && <span className="err">{errors.email}</span>}
        </div>

        <div className="field span-2">
          <label htmlFor="cf-subject">Subject *</label>
          <input
            id="cf-subject"
            name="subject"
            type="text"
            placeholder="How can we help?"
            value={values.subject}
            onChange={onChange}
            className="box-border w-full min-w-0"
            aria-invalid={!!errors.subject}
          />
          {errors.subject && <span className="err">{errors.subject}</span>}
        </div>

        <div className="field span-2">
          <label htmlFor="cf-message">Message *</label>
          <textarea
            id="cf-message"
            name="message"
            maxLength={MAX_MESSAGE}
            placeholder="Tell us a little about your enquiry"
            value={values.message}
            onChange={onChange}
            className="box-border w-full min-w-0"
            aria-invalid={!!errors.message}
          />
          {errors.message && <span className="err">{errors.message}</span>}
        </div>

        <div className="field hp-field" aria-hidden="true">
          <label htmlFor="cf-contact-alt">Contact reference</label>
          <input
            id="cf-contact-alt"
            name="contact_alt"
            type="text"
            tabIndex={-1}
            autoComplete="off"
            readOnly
          />
        </div>
      </div>

      <div className="form-foot">
        <span className="count">{values.message.length} / {MAX_MESSAGE} characters</span>
        <button type="submit" className="submit-btn" disabled={status === 'sending'}>
          {status === 'sending' ? 'Sending…' : 'Submit Message'}
          <i className="ri-arrow-right-line" aria-hidden="true" />
        </button>
      </div>

      {status === 'ok' && (
        <div className="status ok" role="status">
          {feedback}
        </div>
      )}
      {status === 'error' && (
        <div className="status bad" role="alert">
          {feedback}{' '}
          <a href="mailto:sherri@therevefoundation.com" style={{ color: 'inherit', fontWeight: 500 }}>
            Email Sherri Davis directly
          </a>
        </div>
      )}
    </form>
  );
}